import { useNavigate } from "react-router-dom";
import PageHeading from "../../Components/PageHeading/PageHeading";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import "./PhotoPosterAi.css";
import SamplePdfModal from "../../Components/SamplePdfModal/SamplePdfModal";
import ContentPhotoPosterAi from "../Gallery/ContentPhotoPosterAi";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";

const aspectRatioOptions = [
  { value: "1:1", label: "1:1", description: "Square poster" },
  { value: "3:4", label: "3:4", description: "Classic vertical poster" },
  { value: "4:5", label: "4:5", description: "Social media poster" },
  { value: "2:3", label: "2:3", description: "Tall print-style poster" },
  { value: "9:16", label: "9:16", description: "Mobile / Story / TikTok" },
  { value: "16:9", label: "16:9", description: "Wide cinematic poster" },
  { value: "21:9", label: "21:9", description: "Ultra-wide movie banner" },
  { value: "3:2", label: "3:2 / 4:3", description: "General design / print layout" },
];

const PhotoPosterAi = () => {
  const [productImage, setProductImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [headlineText, setHeadlineText] = useState("");
  const [subText, setSubText] = useState("");
  const [aspectRatio, setAspectRatio] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const fileInputRef = useRef(null);
  const { AIuser, userRefetch, setUserRefetch } = useContext(UserAuthContext);
  const navigate = useNavigate();

  const creditToLocalString = AIuser?.credits
    ? parseInt(Math.floor(AIuser?.credits)).toLocaleString()
    : "0";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setProductImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleGenerate = async () => {
    if (AIuser.credits < 75) {
      Swal.fire({
        text: "You don't have enough credits! Please buy credits to continue.",
        icon: "error",
        confirmButtonText: "Buy",
        customClass: {
          popup: "modal_class_success",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/purchase");
        }
      });
      return;
    }

    if (!productImage) {
      Swal.fire({
        text: "Please upload a product image.",
        icon: "warning",
        confirmButtonText: "Ok",
        customClass: { popup: "modal_class_success" },
      });
      return;
    }

    if (!headlineText.trim()) {
      Swal.fire({
        text: "Please enter a headline text.",
        icon: "warning",
        confirmButtonText: "Ok",
        customClass: { popup: "modal_class_success" },
      });
      return;
    }

    if (!aspectRatio) {
      Swal.fire({
        text: "Please select an aspect ratio.",
        icon: "warning",
        confirmButtonText: "Ok",
        customClass: { popup: "modal_class_success" },
      });
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("email", AIuser?.email);
      formData.append("userId", AIuser?._id);
      formData.append("credit", 75);
      formData.append("headlineText", headlineText);
      formData.append("subText", subText);
      formData.append("aspectRatio", aspectRatio);
      formData.append("productImage", productImage);

      const response = await fetch(
        "https://backend.whalesai.com/api/v1/photo-poster-ai/generate",
        {
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("aiUser")}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate Photo Poster");
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Generation failed");
      }

      // await handleSubmitCredits();
      setUserRefetch(!userRefetch)
      setResultImage(data?.data?.imageUrl);

      // Reset form
      setProductImage(null);
      setImagePreview(null);
      setHeadlineText("");
      setSubText("");
      setAspectRatio("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await Swal.fire({
        html: `
    <p class="text-white">
      Your poster has been generated successfully<br/>
      You can view and download it below.
    </p>
  `,
        icon: "success",
        confirmButtonText: "Close",
        customClass: { popup: "modal_class_success" },
      });
    } catch (error) {
      console.error("Error generating Photo Poster:", error);

      Swal.fire({
        text: error.message || "Something went wrong!",
        icon: "error",
        confirmButtonText: "Ok",
        customClass: {
          popup: "modal_class_success",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async ({ imageUrl }) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const fileName = imageUrl.split("/").pop();
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Image download failed:", error);
    }
  };

  const handleClear = () => {
    setProductImage(null);
    setImagePreview(null);
    setHeadlineText("");
    setSubText("");
    setAspectRatio("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isFormValid =
    productImage && headlineText.trim() && aspectRatio;

  return (
    <Fragment>
      {/* <PageHeading
        title={"POSTER DESIGN AI"}
        description={
          "Turn product photos into stunning AI posters in seconds."
        }
        onInfoClick={() => setShowPdfModal(true)}
        infoTooltip="See sample photo poster"
      /> */}

      <SamplePdfModal pdfModal={showPdfModal} setPdfModal={setShowPdfModal} title="What you can do?">
        <ContentPhotoPosterAi />
      </SamplePdfModal>

      <div className="container pb-5"
        style={{
          maxWidth: "872px",
          paddingInline: "12px",
          marginInline: "auto",
        }}
      >
        <div className="container py-2">
          {/* Credits section */}
          <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
            <div>
              {/* <div>
                <button className="credits-button">
                  CREDITS : {creditToLocalString}
                </button>
                <button
                  className="buy-credits-button"
                  onClick={() => navigate("/purchase")}
                >
                  BUY CREDITS
                </button>
                <button
                  onClick={() => navigate("/usage")}
                  className="money-exchange-button"
                >
                  <img src={moneyExchangeIcon} alt="usage" />
                </button>
              </div> */}
              <p className="all-page-title all-page-gradient-text text-center my-4">POSTER DESIGN AI</p>
              <div className="credits-actions">

                <button className="credits-button">
                  CREDITS : {creditToLocalString}
                </button>

                <button
                  className="buy-credits-button"
                  onClick={() => navigate("/purchase")}
                >
                  BUY CREDITS
                </button>

                <button
                  onClick={() => navigate("/usage")}
                  className="money-exchange-button"
                >
                  <img src={moneyExchangeIcon} alt="usage" />
                </button>

                <button
                  type="button"
                  className="money-exchange-button"
                  onClick={() => {
                    setShowPdfModal(true);
                  }}

                >
                  <img src={sampleIcon} alt="See sample video" />
                </button>

              </div>
              <p
                style={{ fontSize: "8px" }}
                className="text-white mt-2 creditText text-center"
              >
                75 Credits (USD 0.90 based on Credits Package 3)
              </p>
            </div>
          </div>

          {/* Step 1: Upload Product Image */}
          <div className="mb-4 mt-4">
            <div className="photo-poster-step-label">Step 1</div>
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Upload Product Image <span className="text-danger">*</span>
            </label>
            <div
              className={`photo-poster-upload-area ${imagePreview ? "has-image" : ""}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Product preview"
                    className="photo-poster-preview-img"
                  />
                  <button
                    className="photo-poster-remove-btn"
                    onClick={handleRemoveImage}
                    title="Remove image"
                  >
                    ✕
                  </button>
                </>
              ) : (
                <>
                  <div className="photo-poster-upload-icon">📷</div>
                  <div className="photo-poster-upload-text">
                    Drag & drop your image here or <span>browse</span>
                  </div>
                  <div
                    className="photo-poster-upload-text mt-1"
                    style={{ fontSize: "12px" }}
                  >
                    Supports JPG, PNG, WEBP
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Step 2: Enter Text */}
          <div className="mb-4">
            <div className="photo-poster-step-label">Step 2</div>
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Headline Text <span className="text-danger">*</span>
            </label>
            <textarea
              className="p-2 px-3 w-100 text-white"
              style={{
                background: "#011C34",
                border: "2px solid #FFF",
                borderRadius: "12px",
                outline: "none",
                height: "80px",
                resize: "none",
              }}
              value={headlineText}
              onChange={(e) => setHeadlineText(e.target.value)}
              placeholder="Enter headline text..."
            />
          </div>

          <div className="mb-4">
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Sub Text
            </label>
            <textarea
              className="p-2 px-3 w-100 text-white"
              style={{
                background: "#011C34",
                border: "2px solid #FFF",
                borderRadius: "12px",
                outline: "none",
                height: "80px",
                resize: "none",
              }}
              value={subText}
              onChange={(e) => setSubText(e.target.value)}
              placeholder="Enter sub text (optional)..."
            />
          </div>

          {/* Step 3: Aspect Ratio */}
          <div className="mb-4">
            <div className="photo-poster-step-label">Step 3</div>
            <label className="text-white mb-2" style={{ fontSize: "14px" }}>
              Aspect Ratio <span className="text-danger">*</span>
            </label>
            <div className="photo-poster-ratio-grid">
              {aspectRatioOptions.map((option) => (
                <div
                  key={option.value}
                  className={`photo-poster-ratio-card ${aspectRatio === option.value ? "selected" : ""}`}
                  onClick={() => setAspectRatio(option.value)}
                >
                  <div className="ratio-value">{option.label}</div>
                  <div className="ratio-label">{option.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* <AiContentAudit checked={auditChecked} onChange={setAuditChecked} /> */}

          {/* Action Buttons */}
          <div className="d-flex justify-content-center align-items-center mb-5">
            <Button
              sx={{
                padding: "8px 30px",
                marginRight: "10px",
                color: "#000",
                background: "#fff",
                "&:hover": { background: "#fff" },
                "&:disabled": { background: "#fff" },
              }}
              onClick={handleClear}
              disabled={
                (!productImage &&
                  !headlineText &&
                  !subText &&
                  !aspectRatio) ||
                isLoading
              }
              variant="contained"
            >
              Clear
            </Button>
            <Button
              sx={{
                padding: "8px 30px",
                "&:disabled": { background: "gray" },
              }}
              onClick={handleGenerate}
              className="generate-button text-white"
              disabled={!isFormValid || isLoading}
              variant="contained"
            >
              {isLoading ? 'Processing...' : 'Generate'}
            </Button>
          </div>

          <div className="animate text-md-center text-lg-center text-center mb-5 d-flex justify-content-center force-small-font">
            Please do not navigate away from the browser.
            <br /> Doing so will stop the process.
          </div>
          <div className="text-md-center text-white text-lg-center text-center d-flex justify-content-center force-small-font mt-1">
            While AI strives for accuracy, it may occasionally make mistakes. <br />Please verify any important information.
          </div>
          <div className="d-flex justify-content-center mt-3">
            {isLoading && (
              <div className="spinner-border text-light" role="status" style={{ width: '4rem', height: '4rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
          </div>

          {/* Interesting facts while generating */}
          {isLoading && <AiFactsDisplay />}

          {resultImage && (
            <div className="mt-3 d-flex flex-column align-items-center justify-content-center">
              <p className="text-white">Generated Image</p>
              <img src={resultImage} alt="Generated" className="img-fluid" />
            </div>
          )}

          {
            resultImage && (
              <div>
                <button
                  className="btn btn-success mt-3"
                  onClick={() => {
                    handleDownload({ imageUrl: resultImage })
                  }}
                >
                  <i className="fa-solid fa-download"></i>
                  {' '}
                  Download
                </button>
              </div>
            )
          }

          <div className="mt-3">
            <p className='text-warning'>Sample Image</p>
            <img src={"/photo-poster-sample.png"} alt="Sample" className="img-fluid" />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PhotoPosterAi;